import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod/v4';
import { PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table';
import {
  type IxfWatchlistV1Fincen314EntryQueryServiceFincen314EntryListFilterableFields,
  IxfWatchlistV1Fincen314EntryQueryServiceFincen314EntryListSearchableFields,
} from '@interxfi/types/generated/api';
import { PageTabRail } from '../../../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../../components/router/router-tabbed-navigation';
import { FincenEntriesTable } from '../../../../../../../../../../features/watchlist/fincen/entry/fincen-entry-table';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';
import { tableSearchParamsSchema, useTableSearchParams } from '../../../../../../../../../../util/table-search-params';

const searchSchema = z.object({
  ...tableSearchParamsSchema.shape,
  searchField: z.enum(IxfWatchlistV1Fincen314EntryQueryServiceFincen314EntryListSearchableFields).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/compliance/_compliance/fincen/entry/_entry/')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.ClearingComplianceFincen,
      label: 'Entries',
      order: 1,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.ClearingComplianceFincen>,
  validateSearch: searchSchema,
  head: () => getPageHead('FinCEN Entries'),
  component: FincenEntryList,
});

function TableHeader({
  filter,
  search,
}: CustomTableHeaderProps<
  IxfWatchlistV1Fincen314EntryQueryServiceFincen314EntryListFilterableFields,
  IxfWatchlistV1Fincen314EntryQueryServiceFincen314EntryListSearchableFields
>) {
  return (
    <VerticalStack gap={7}>
      <PageTabRail
        actions={
          <VerticalStack gap={7}>
            <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
              {filter}
            </HorizontalStack>
          </VerticalStack>
        }
        group={TabbedNavigationGroup.ClearingComplianceFincen}
      />
      {search}
    </VerticalStack>
  );
}

function FincenEntryList() {
  const { initialSearchValue, initialSearchField } = useTableSearchParams<IxfWatchlistV1Fincen314EntryQueryServiceFincen314EntryListSearchableFields>(
    Route.useSearch(),
  );

  return (
    <PageLayout>
      <PageLayout.Section noBorder>
        <FincenEntriesTable
          HeaderActions={TableHeader}
          initialSearchFields={initialSearchField ? [initialSearchField] : undefined}
          initialSearchValue={initialSearchValue}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
